/**
 * t369u: per-workspace modules on the web (packages/shared/src/modules.ts). The off list comes with my permissions
 * (GET /me/permissions `modules_off`), so it is cached and refreshed with them. Switched-off modules are hidden
 * everywhere; a deep link to one shows <ModuleOff /> instead of the page.
 */
import type { ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { MODULE_OFF_TITLE, isModuleOn, moduleForPath, moduleOffMessage, settingsPathModuleOff, type ModuleId } from '@tradeoutlet/shared';
import { usePermissions } from './permissions';

const NONE: ModuleId[] = [];
export function useModulesOff(): ModuleId[] { return usePermissions().data?.modules_off ?? NONE; }
export function useModuleOn(id: ModuleId | null | undefined): boolean { return isModuleOn(useModulesOff(), id); }

export function ModuleOff({ module }: { module: ModuleId | null }) {
  return (
    <div className="flex h-full items-center justify-center p-8" data-testid="module-off">
      <div className="max-w-sm text-center">
        <h1 className="text-lg font-semibold text-slate-900 dark:text-white">{MODULE_OFF_TITLE}</h1>
        <p className="mt-1 text-sm text-slate-500">{moduleOffMessage(module)}</p>
        <Link to="/" className="mt-4 inline-block rounded-md bg-brand-yellow px-3 py-1.5 text-sm font-medium text-on-accent-strong">Back to Home</Link>
      </div>
    </div>
  );
}

/** The page, or "Not available" when its module is switched off for the workspace. */
export function ModuleRoute({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  const off = useModulesOff();
  const page = moduleForPath(pathname);
  const module = page && !isModuleOn(off, page) ? page : settingsPathModuleOff(off, pathname);
  return <>{module ? <ModuleOff module={module} /> : children}</>;
}
